// src/pages/MyOrders.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

const DEFAULT_LIMIT = 10;

export default function MyOrders() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");
  const [orders, setOrders] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  const money = (v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return "0.00";
    return n.toFixed(2);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  const pillStyle = (status, paymentStatus) => {
    const s = String(status || "").toUpperCase();
    const p = String(paymentStatus || "").toUpperCase();
    if (p === "SUCCESS")
      return { bg: "rgba(34,197,94,.12)", bd: "rgba(34,197,94,.26)", tx: "#065f46", label: "PAID" };
    if (p === "FAILED")
      return { bg: "rgba(239,68,68,.10)", bd: "rgba(239,68,68,.22)", tx: "#7f1d1d", label: "FAILED" };
    if (s === "DELIVERED")
      return { bg: "rgba(6,182,212,.12)", bd: "rgba(6,182,212,.22)", tx: "#155e75", label: "DELIVERED" };
    return {
      bg: "rgba(245,158,11,.12)",
      bd: "rgba(245,158,11,.25)",
      tx: "#92400e",
      label: p === "PENDING" ? "PENDING" : s || "PENDING",
    };
  };

  // ── initial load (page 1) ──────────────────────────────────────────────────
  const loadOrders = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await axiosInstance.get("/api/orders", {
        params: { limit: DEFAULT_LIMIT, page: 1 },
      });
      const d = res?.data || {};
      const list = d.orders || d.data || [];
      const serverTotal = d.total ?? d.totalCount ?? d.count ?? list.length;
      setOrders(Array.isArray(list) ? list : []);
      setTotal(Number(serverTotal) || 0);
      setPage(1);
      setHasMore(list.length >= DEFAULT_LIMIT && list.length < Number(serverTotal));
    } catch (e) {
      setErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Failed to load orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // ── load more (next pages) ─────────────────────────────────────────────────
  const loadMore = async () => {
    const nextPage = page + 1;
    try {
      setLoadingMore(true);
      const res = await axiosInstance.get("/api/orders", {
        params: { limit: DEFAULT_LIMIT, page: nextPage },
      });
      const d = res?.data || {};
      const list = d.orders || d.data || [];
      const serverTotal = d.total ?? d.totalCount ?? d.count ?? 0;
      setOrders((prev) => [...prev, ...(Array.isArray(list) ? list : [])]);
      setPage(nextPage);
      const loaded = orders.length + list.length;
      setHasMore(list.length >= DEFAULT_LIMIT && loaded < Number(serverTotal));
      setTotal(Number(serverTotal) || 0);
    } catch (e) {
      setErr(e?.response?.data?.msg || e?.response?.data?.message || e?.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login", { replace: true }); return; }
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const totalOrders = total || orders.length;
    const totalItems = orders.reduce((s, o) => s + Number((o?.OrderItems || []).length || 0), 0);
    const totalAmount = orders.reduce((s, o) => s + Number(o?.totalAmount || 0), 0);
    return { totalOrders, totalItems, totalAmount };
  }, [orders, total]);

  return (
    <div style={S.page}>
      <style>{css}</style>

      <div style={S.container}>

        {/* ── SUMMARY ───────────────────────────────── */}
        <div className="sumGrid" style={S.summary}>
          <div style={S.sumCard}>
            <div style={S.sumLabel}>Total Orders</div>
            <div style={S.sumValue}>{totals.totalOrders}</div>
          </div>
          <div style={S.sumCard}>
            <div style={S.sumLabel}>Total Paid</div>
            <div style={S.sumValue}>₹ {money(totals.totalAmount)}</div>
          </div>
        </div>

        {/* ── SHOWING INFO + REFRESH ─────────────────── */}
        {!loading && orders.length > 0 && (
          <div style={S.showingRow}>
            <span style={S.showingText}>
              Showing <b>{orders.length}</b>{total ? <> of <b>{total}</b></> : null} orders
            </span>
            <button style={S.refreshBtn} onClick={loadOrders} disabled={loading}>
              ↺ Refresh
            </button>
          </div>
        )}

        {loading && (
          <div style={S.info}>
            <span style={S.spinner} /> Loading orders…
          </div>
        )}
        {err && <div style={{ ...S.info, ...S.infoErr }}>{err}</div>}
        {!loading && !err && orders.length === 0 && <div style={S.info}>No orders found.</div>}

        {/* ── ORDERS LIST ───────────────────────────── */}
        {!loading && !err && orders.length > 0 && (
          <>
            <div style={S.list}>
              {orders.map((o) => {
                const id = o?.id;
                const addr = o?.Address || null;
                const items = o?.OrderItems || [];
                const isOpen = String(openId) === String(id);
                const pill = pillStyle(o?.status, o?.paymentStatus);
                const paidAmount = money(o?.paymentStatus === "SUCCESS" ? o?.totalAmount : 0);

                return (
                  <div key={id} style={S.orderCard}>
                    {/* TOP */}
                    <div className="orderTopGrid" style={S.orderTop}>
                      <div style={{ minWidth: 0 }}>
                        <div style={S.orderTitleRow}>
                          <div style={S.orderTitle}>Order #{id}</div>
                          <span style={{ ...S.pill, background: pill.bg, borderColor: pill.bd, color: pill.tx }}>
                            {pill.label}
                          </span>
                        </div>
                        <div style={S.orderMeta}>
                          <span><b>Method:</b> {o?.paymentMethod || "—"}</span>
                          <span><b>Payment:</b> {o?.paymentStatus || "—"}</span>
                          <span><b>Status:</b> {o?.status || "—"}</span>
                        </div>
                        <div style={S.orderMeta2}>
                          <span><b>Created:</b> {fmtDate(o?.createdAt)}</span>
                          <span><b>Delivered:</b> {o?.deliveredOn ? fmtDate(o?.deliveredOn) : "—"}</span>
                        </div>
                      </div>

                      <div style={S.amountBox}>
                        {(() => {
                          const itemTotal = (o?.OrderItems || []).reduce((s, it) => {
                            const p = it?.Product || {};
                            const price = Number(it?.price || p?.price || 0);
                            const qty = Number(it?.qty || 0);
                            return s + price * qty;
                          }, 0);
                          const finalPrice = Number(o?.totalAmount || 0);
                          const saved = itemTotal - finalPrice;

                          return (
                            <>
                              <div style={S.amountLabel}>Total Value</div>
                              <div style={S.amountValue}>₹ {money(itemTotal)}</div>
                              {saved > 0 && (
                                <div style={S.amountSaved}>You Saved: ₹ {money(saved)}</div>
                              )}
                              <div style={S.amountDivider} />
                              <div style={S.amountSub}>
                                Paid: <b style={{ color: "#065f46" }}>₹ {money(finalPrice)}</b>
                              </div>
                            </>
                          );
                        })()}
                        <button type="button" style={S.smallBtn} onClick={() => setOpenId(isOpen ? null : id)}>
                          {isOpen ? "Hide Details" : "View Details"}
                        </button>
                      </div>
                    </div>

                    {/* DETAILS */}
                    {isOpen && (
                      <div className="detailsGrid" style={S.detailsWrap}>
                        {/* ADDRESS */}
                        <div style={S.detailsCard}>
                          <div style={S.detailsTitle}>Delivery Address</div>
                          {!addr ? (
                            <div style={{ color: "rgba(15,23,42,.70)" }}>No address</div>
                          ) : (
                            <div style={S.addrText}>
                              <div style={{ fontWeight: 950 }}>
                                {addr.label || "Address"}
                                {addr.isDefault ? <span style={S.pillTiny}>DEFAULT</span> : null}
                              </div>
                              <div style={{ marginTop: 8, lineHeight: 1.5, color: "rgba(15,23,42,.78)" }}>
                                {addr.receiverFirstName} {addr.receiverLastName || ""} • {addr.receiverPhone}
                                <br />
                                {addr.house}, {addr.area}
                                {addr.landmark ? `, ${addr.landmark}` : ""} - {addr.pincode}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* ITEMS */}
                        <div style={S.detailsCard}>
                          <div style={S.detailsTitle}>Items ({items.length})</div>
                          {items.length === 0 ? (
                            <div style={{ color: "rgba(15,23,42,.70)" }}>No items</div>
                          ) : (
                            <div style={S.itemsGrid}>
                              {items.map((it) => {
                                const p = it?.Product || {};
                                const qty = Number(it?.qty || 0);
                                const price = Number(it?.price || p?.price || 0);
                                const lineTotal = price * qty;
                                return (
                                  <div key={it?.id} style={S.itemRowTextOnly}>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={S.itemNameText} title={p?.name || ""}>{p?.name || "—"}</div>
                                      <div style={S.itemSubText}>
                                        {p?.brand ? p.brand : "—"} {p?.category ? `• ${p.category}` : ""}
                                      </div>
                                    </div>
                                    <div style={S.itemRight}>
                                      <div style={S.itemQtyPill}>Qty: {qty}</div>
                                      <div style={S.itemLineTotal}>₹ {money(lineTotal)}</div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── LOAD MORE ─────────────────────────── */}
            {hasMore && (
              <div style={S.loadMoreWrap}>
                <button
                  style={loadingMore ? { ...S.loadMoreBtn, ...S.loadMoreBtnDisabled } : S.loadMoreBtn}
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <><span style={S.spinner} /> Loading…</>
                  ) : (
                    <>Load More Orders ({orders.length} / {total})</>
                  )}
                </button>
              </div>
            )}

            {/* ── END MESSAGE ──────────────────────── */}
            {!hasMore && orders.length > 0 && (
              <div style={S.endMsg}>
                ✓ All {orders.length} order{orders.length !== 1 ? "s" : ""} loaded
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const css = `
  *{ margin:0; padding:0; box-sizing:border-box; }
  html,body,#root{ height:100%; width:100%; }
  body{
    overflow-x:hidden;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto;
    background: #ffffff;
    color: #0f172a;
  }
  button{ font-family: inherit; }

  .sumGrid{
    display:grid;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap:12px;
  }
  @media (max-width: 480px){
    .sumGrid{ gap:10px !important; }
  }
  @media (max-width: 900px){
    .orderTopGrid{ grid-template-columns: 1fr !important; }
    .detailsGrid{ grid-template-columns: 1fr !important; }
  }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

/* ─── STYLES ──────────────────────────────────────────────────────────────── */
const S = {
  page: { minHeight: "100vh", color: "#0f172a", background: "#ffffff" },
  container: { width: "min(1200px, 100%)", margin: "0 auto", padding: "18px 16px 60px" },

  info: {
    padding: "12px 14px",
    borderRadius: 12,
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,.10)",
    boxShadow: "0 10px 22px rgba(0,0,0,.06)",
    marginBottom: 10,
    color: "rgba(15,23,42,.75)",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  infoErr: {
    borderColor: "rgba(239,68,68,.25)",
    background: "rgba(239,68,68,.06)",
    color: "#7f1d1d",
  },

  spinner: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid rgba(15,23,42,.12)",
    borderTopColor: "#2563eb",
    borderRadius: "50%",
    animation: "spin .7s linear infinite",
    flexShrink: 0,
  },

  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 14,
  },
  sumCard: {
    borderRadius: 16,
    padding: 14,
    border: "1px solid rgba(15,23,42,.10)",
    background: "#ffffff",
    boxShadow: "0 10px 22px rgba(0,0,0,.06)",
    minWidth: 0,
  },
  sumLabel: { fontSize: 12, color: "rgba(15,23,42,.60)", fontWeight: 700 },
  sumValue: { fontSize: 20, fontWeight: 800, marginTop: 6, color: "#0f172a" },

  showingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
    padding: "8px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.08)",
    background: "rgba(15,23,42,.02)",
  },
  showingText: { fontSize: 13, color: "rgba(15,23,42,.65)" },
  refreshBtn: {
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.08)",
    color: "#1d4ed8",
    borderRadius: 10,
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },

  list: { display: "grid", gap: 12 },

  orderCard: {
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,.10)",
    background: "#ffffff",
    boxShadow: "0 12px 28px rgba(0,0,0,.06)",
    overflow: "hidden",
  },

  orderTop: {
    padding: 14,
    display: "grid",
    gridTemplateColumns: "1fr 220px",
    gap: 14,
    alignItems: "start",
  },

  orderTitleRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  orderTitle: { fontSize: 17, fontWeight: 800, color: "#0f172a" },

  pill: {
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    border: "1px solid rgba(15,23,42,.10)",
    background: "rgba(15,23,42,.03)",
  },

  orderMeta: {
    marginTop: 10,
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    fontSize: 12,
    color: "rgba(15,23,42,.75)",
  },
  orderMeta2: {
    marginTop: 8,
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    fontSize: 12,
    color: "rgba(15,23,42,.65)",
  },

  amountBox: {
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,.10)",
    background: "rgba(15,23,42,.03)",
    padding: 12,
    display: "grid",
    gap: 5,
  },
  amountLabel: { fontSize: 11, color: "rgba(15,23,42,.55)", fontWeight: 700 },
  amountValue: { fontSize: 17, fontWeight: 800, color: "#0f172a" },
  amountSaved: { fontSize: 13, color: "#059669", fontWeight: 800, marginTop: 4 },
  amountDivider: { height: 1, background: "rgba(15,23,42,.08)", margin: "8px 0" },
  amountSub: { fontSize: 12, color: "rgba(15,23,42,.75)", fontWeight: 700 },

  smallBtn: {
    marginTop: 4,
    height: 38,
    borderRadius: 10,
    border: "1px solid rgba(37,99,235,.25)",
    background: "rgba(37,99,235,.08)",
    color: "#1d4ed8",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 12,
  },

  detailsWrap: {
    borderTop: "1px solid rgba(15,23,42,.08)",
    padding: 14,
    display: "grid",
    gridTemplateColumns: "340px 1fr",
    gap: 12,
    background: "rgba(15,23,42,.02)",
  },

  detailsCard: {
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,.10)",
    background: "#ffffff",
    boxShadow: "0 8px 18px rgba(0,0,0,.05)",
    padding: 12,
  },
  detailsTitle: { fontSize: 13, fontWeight: 800, marginBottom: 10, color: "#0f172a" },

  addrText: { fontSize: 13, color: "#0f172a" },
  pillTiny: {
    marginLeft: 8,
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.10)",
    color: "#1d4ed8",
  },

  itemsGrid: { display: "grid", gap: 8 },

  itemRowTextOnly: {
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.08)",
    background: "#fafafa",
    padding: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  itemNameText: {
    fontWeight: 700,
    fontSize: 13,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 480,
  },
  itemSubText: {
    marginTop: 4,
    fontSize: 11,
    color: "rgba(15,23,42,.60)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemRight: { display: "grid", gap: 5, justifyItems: "end", flexShrink: 0 },
  itemQtyPill: {
    padding: "3px 10px",
    borderRadius: 999,
    border: "1px solid rgba(37,99,235,.22)",
    background: "rgba(37,99,235,.10)",
    color: "#1d4ed8",
    fontWeight: 800,
    fontSize: 11,
    whiteSpace: "nowrap",
  },
  itemLineTotal: { fontWeight: 800, color: "#0f172a", fontSize: 13 },

  loadMoreWrap: {
    display: "flex",
    justifyContent: "center",
    marginTop: 20,
  },
  loadMoreBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 32px",
    borderRadius: 14,
    border: "1px solid rgba(37,99,235,.25)",
    background: "rgba(37,99,235,.08)",
    color: "#1d4ed8",
    fontWeight: 800,
    fontSize: 14,
    cursor: "pointer",
    transition: "background .15s",
  },
  loadMoreBtnDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },

  endMsg: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 13,
    color: "rgba(15,23,42,.45)",
    fontWeight: 600,
    padding: "10px 0",
  },
};